import { getRolePrefix, RoutesLink } from '@/utility/RouteLinks';
import { Navigate, Route, Routes } from 'react-router-dom';
import PrivateRoute from './PrivateRouting';
import ProtectedLoginRoute from './ProtectedLoginRoute';
import PermissionRoute from './PermissionRoute';
import { RESOURCES } from '@/context/permissions';
import LoginPage from '@/pages/Auth/LoginPage';
import ForgotPassword from '@/pages/Auth/ForgotPassword';
import ResetPassword from '@/pages/Auth/ResetPassword';
import DashboardPage from '@/pages/Dashboard/DashboardPage';
import Forbidden403 from '@/pages/ErrorPages/Forbidden403';
import { Category } from '@/pages/BusinessAdmin/Category/Category';
import { Varients } from '@/pages/BusinessAdmin/Varients/Varients';
import { StoreDetails } from '@/pages/BusinessAdmin/Stores/StoreDetails';
import { AddVarients } from '@/pages/BusinessAdmin/Varients/AddVarients';
import { Products } from '@/pages/BusinessAdmin/Products/Products';
import { ImportProducts } from '@/pages/BusinessAdmin/Products/ImportProducts';
import { AddProduct } from '@/pages/BusinessAdmin/Products/AddProduct';
import { Discount } from '@/pages/BusinessAdmin/Discount/Discount';
import { AddDiscount } from '@/pages/BusinessAdmin/Discount/AddDiscount';
import { ViewDiscount } from '@/pages/BusinessAdmin/Discount/ViewDiscount';
import { Coupon } from '@/pages/BusinessAdmin/Coupon/Coupon';
import { AddCoupon } from '@/pages/BusinessAdmin/Coupon/AddCoupon';
import { CouponView } from '@/pages/BusinessAdmin/Coupon/CouponView';
import { ProductView } from '@/pages/BusinessAdmin/Products/ProductView';
import { SettingsPage } from '@/pages/Settings/SettingsPage';
import { Orders } from '@/pages/BusinessAdmin/Orders/Orders';
import { OrdersView } from '@/pages/BusinessAdmin/Orders/OrdersView';
import NotificationsPage from '@/pages/Notifications/NotificationsPage';
import CreateNotification from '@/pages/Notifications/CreateNotification';
import { Drivers } from '@/pages/BusinessAdmin/Drivers/Drivers';

import { DriverDetails } from '@/pages/BusinessAdmin/Drivers/DriverDetails';
import { Customers } from '@/pages/BusinessAdmin/Customers/Customers';
import { CustomerDetails } from '@/pages/BusinessAdmin/Customers/CustomerDetails';
import { Roles } from '@/pages/BusinessAdmin/Roles/Roles';
import { AddEditRole } from '@/pages/BusinessAdmin/Roles/AddEditRole';
import { HomeLayout } from '@/pages/BusinessAdmin/HomeLayout/HomeLayout';
import { ViewRole } from '@/pages/BusinessAdmin/Roles/ViewRole';
import { AddOns } from '@/pages/BusinessAdmin/AddOns/AddOns';
import { AddAddOns } from '@/pages/BusinessAdmin/AddOns/AddAddOns';
import LiveOrders from '@/pages/BusinessAdmin/Orders/LiveOrdes';
import Staffs from '@/pages/BusinessAdmin/Staffs/Staffs';
import AddEditStaff from '@/pages/BusinessAdmin/Staffs/AddEditStaff';
import { Reviews } from '@/pages/BusinessAdmin/Reviews/Reviews';
import { Tags } from '@/pages/BusinessAdmin/Tags/Tags';
import { CashSettlement } from '@/pages/BusinessAdmin/CashSettlement/CashSettlement';
import { StoreSettlements } from '@/pages/BusinessAdmin/StoreSettlements/StoreSettlements';
import { CreateStoreSettlement } from '@/pages/BusinessAdmin/StoreSettlements/CreateStoreSettlement';
import Zones from '@/pages/BusinessAdmin/Zones/Zones';
import AddZone from '@/pages/BusinessAdmin/Zones/AddZone';
import { useActiveRole } from '@/hooks/useActiveRole';
import AddDriver from '@/pages/BusinessAdmin/Drivers/AddDriver';
import { Return } from '@/pages/BusinessAdmin/ReturnOrder/ReturnOrders';
import { ReturnDetails } from '@/pages/BusinessAdmin/ReturnOrder/ReturnDetails';
import { ReturnReasons } from '@/pages/BusinessAdmin/ReturnReasons/ReturnReasons';
import AnalyticsPage from '@/pages/Dashboard/AnalyticsPage';
import { Payouts } from '@/pages/BusinessAdmin/Payouts/Payouts';
import { usePermissions } from '@/hooks/usePermissions';
import { Stores } from '@/pages/BusinessAdmin/Stores/Stores';
import { AddStore } from '@/pages/BusinessAdmin/Stores/AddStore';
import { FAQPage } from '@/pages/Public/FAQPage';
import { DisclaimerPage } from '@/pages/Public/DisclaimerPage';
import { PrivacyPolicyPage } from '@/pages/Public/PrivacyPolicyPage';
import { ReturnPolicyPage } from '@/pages/Public/ReturnPolicyPage';
import { TermsConditionsPage } from '@/pages/Public/TermsConditionsPage';


interface IPrivateRoutes {
  path: string;
  route: React.ReactNode;
}

export default function CommonRouts() {
  const { prefix } = useActiveRole();
  const { isAdmin } = usePermissions();
  const ProtectedLoginRoutes = [
    {
      path: RoutesLink.login,
      route: <LoginPage />,
    },
    {
      path: RoutesLink.forgotPassword,
      route: <ForgotPassword />,
    },
    {
      path: `${RoutesLink.resetPassword}/:token`,
      route: <ResetPassword />,
    },
  ];

  const privateRoutes: IPrivateRoutes[] = [
    {
      path: prefix + RoutesLink.dashboard,
      route: <PermissionRoute element={<DashboardPage />} resource={RESOURCES.DASHBOARD} action="view" />,
    },
    // ── Stores ───────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.stores,
      route: <PermissionRoute element={<Stores />} resource={RESOURCES.STORE} action="view" />,
    },
    {
      path: prefix + RoutesLink.AddStore,
      route: <PermissionRoute element={<AddStore />} resource={RESOURCES.STORE} action="create" />,
    },
    {
      path: prefix + RoutesLink.EditStore,
      route: <PermissionRoute element={<AddStore />} resource={RESOURCES.STORE} action="update" />,
    },
    {
      path: prefix + RoutesLink.storeDetails,
      route: <PermissionRoute element={<StoreDetails />} resource={RESOURCES.STORE} action="view" />,
    },
    // ── Categories ────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Category,
      route: <PermissionRoute element={<Category />} resource={RESOURCES.CATEGORY} action="view" />,
    },
    // ── Variants ──────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Varients,
      route: <PermissionRoute element={<Varients />} resource={RESOURCES.VARIANT} action="view" />,
    },
    {
      path: prefix + RoutesLink.AddVarients,
      route: <PermissionRoute element={<AddVarients />} resource={RESOURCES.VARIANT} action="create" />,
    },
    {
      path: prefix + RoutesLink.EditVarients,
      route: <PermissionRoute element={<AddVarients />} resource={RESOURCES.VARIANT} action="update" />,
    },
    // ── Add-Ons ───────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.AddOns,
      route: <PermissionRoute element={<AddOns />} resource={RESOURCES.ADDON} action="view" />,
    },
    {
      path: prefix + RoutesLink.AddAddOns,
      route: <PermissionRoute element={<AddAddOns />} resource={RESOURCES.ADDON} action="create" />,
    },
    {
      path: prefix + RoutesLink.EditAddOns,
      route: <PermissionRoute element={<AddAddOns />} resource={RESOURCES.ADDON} action="update" />,
    },
    // ── Products ──────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Products,
      route: <PermissionRoute element={<Products />} resource={RESOURCES.PRODUCT} action="view" />,
    },
    {
      path: prefix + RoutesLink.ImportProducts,
      route: <PermissionRoute element={<ImportProducts />} resource={RESOURCES.PRODUCT} action="import-product" />,
    },
    {
      path: prefix + RoutesLink.AddProduct,
      route: <PermissionRoute element={<AddProduct />} resource={RESOURCES.PRODUCT} action="create" />,
    },
    {
      path: prefix + RoutesLink.EditProduct,
      route: <PermissionRoute element={<AddProduct />} resource={RESOURCES.PRODUCT} action="update" />,
    },
    {
      path: prefix + RoutesLink.ViewProduct,
      route: <PermissionRoute element={<ProductView />} resource={RESOURCES.PRODUCT} action="view" />,
    },

    // ── Coupons ───────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Coupons,
      route: <PermissionRoute element={<Coupon />} resource={RESOURCES.COUPON} action="view" />,
    },
    {
      path: prefix + RoutesLink.AddCoupon,
      route: <PermissionRoute element={<AddCoupon />} resource={RESOURCES.COUPON} action="create" />,
    },
    {
      path: prefix + RoutesLink.EditCoupon,
      route: <PermissionRoute element={<AddCoupon />} resource={RESOURCES.COUPON} action="update" />,
    },
    {
      path: prefix + RoutesLink.ViewCoupon,
      route: <PermissionRoute element={<CouponView />} resource={RESOURCES.COUPON} action="view" />,
    },
    // ── Settings ──────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Settings,
      route: <PermissionRoute element={<SettingsPage />} resource={RESOURCES.SETTINGS} action="view" />,
    },
    // ── Orders ────────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Orders,
      route: <PermissionRoute element={<Orders />} resource={RESOURCES.ORDER} action="view" />,
    },
    ...(!isAdmin ? [{
      path: prefix + RoutesLink.LiveOrders,
      route: <PermissionRoute element={<LiveOrders />} resource={RESOURCES.LIVE_ORDER} action="view" />,
    },     // ── Discounts ─────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Discount,
      route: <PermissionRoute element={<Discount />} resource={RESOURCES.DISCOUNT} action="view" />,
    },
    {
      path: prefix + RoutesLink.AddDiscount,
      route: <PermissionRoute element={<AddDiscount />} resource={RESOURCES.DISCOUNT} action="create" />,
    },
    {
      path: prefix + RoutesLink.EditDiscount,
      route: <PermissionRoute element={<AddDiscount />} resource={RESOURCES.DISCOUNT} action="update" />,
    },
    {
      path: prefix + RoutesLink.ViewDiscount,
      route: <PermissionRoute element={<ViewDiscount />} resource={RESOURCES.DISCOUNT} action="view" />,
    },
    ] : []),
    {
      path: prefix + RoutesLink.ViewOrders,
      route: <PermissionRoute element={<OrdersView />} resource={RESOURCES.ORDER} action="view" />,
    },
    // ── Returns ──────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Return,
      route: <PermissionRoute element={<Return />} resource={RESOURCES.RETURNS} action="view" />,
    },
    {
      path: prefix + RoutesLink.ViewReturn,
      route: <PermissionRoute element={<ReturnDetails />} resource={RESOURCES.RETURNS} action="view" />,
    },
    {
      path: prefix + RoutesLink.ReturnReasons,
      route: <PermissionRoute element={<ReturnReasons />} resource={RESOURCES.RETURN_REASON} action="view" />,
    },
    // ── Notifications ─────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Notifications,
      route: <PermissionRoute element={<NotificationsPage />} resource={RESOURCES.NOTIFICATION} action="view" />,
    },
    {
      path: prefix + RoutesLink.CreateNotification,
      route: <PermissionRoute element={<CreateNotification />} resource={RESOURCES.NOTIFICATION} action="create" />,
    },
    // ── Drivers ───────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Drivers,
      route: <PermissionRoute element={<Drivers />} resource={RESOURCES.DRIVER} action="view" />,
    },
    {
      path: prefix + RoutesLink.AddDriver,
      route: <PermissionRoute element={<AddDriver />} resource={RESOURCES.DRIVER} action="create" />,
    },
    {
      path: prefix + RoutesLink.EditDriver,
      route: <PermissionRoute element={<AddDriver />} resource={RESOURCES.DRIVER} action="update" />,
    },
    {
      path: prefix + RoutesLink.DriverDetails,
      route: <PermissionRoute element={<DriverDetails />} resource={RESOURCES.DRIVER} action="view" />,
    },
    // ── Customers ─────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Customers,
      route: <PermissionRoute element={<Customers />} resource={RESOURCES.CUSTOMER} action="view" />,
    },
    {
      path: prefix + RoutesLink.CustomerDetails,
      route: <PermissionRoute element={<CustomerDetails />} resource={RESOURCES.CUSTOMER} action="view" />,
    },
    // ── Roles ─────────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Roles,
      route: <PermissionRoute element={<Roles />} resource={RESOURCES.ROLE} action="view" />,
    },
    {
      path: prefix + RoutesLink.AddRole,
      route: <PermissionRoute element={<AddEditRole />} resource={RESOURCES.ROLE} action="create" />,
    },
    {
      path: prefix + RoutesLink.EditRole,
      route: <PermissionRoute element={<AddEditRole />} resource={RESOURCES.ROLE} action="update" />,
    },
    {
      path: prefix + RoutesLink.ViewRole,
      route: <PermissionRoute element={<ViewRole />} resource={RESOURCES.ROLE} action="view" />,
    },
    // ── Reviews ────────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Reviews,
      route: <PermissionRoute element={<Reviews />} resource={RESOURCES.REVIEW} action="view" />,
    },
    // ── Home Layout (CMS) ───────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.HomeLayout,
      route: <PermissionRoute element={<HomeLayout />} resource={RESOURCES.HOME_LAYOUT} action="view" />,
    },
    // ── Tags ─────────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Tags,
      route: <PermissionRoute element={<Tags />} resource={RESOURCES.TAG} action="view" />,
    },
    // ── Cash Settlement ───────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.CashSettlement,
      route: <PermissionRoute element={<CashSettlement />} resource={RESOURCES.CASH_SETTLEMENT} action="view" />,
    },
    // ── Store Settlement ────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.StoreSettlements,
      route: <PermissionRoute element={<StoreSettlements />} resource={RESOURCES.STORE_SETTLEMENT} action="view" />,
    },
    {
      path: prefix + RoutesLink.CreateStoreSettlement,
      route: <PermissionRoute element={<CreateStoreSettlement />} resource={RESOURCES.STORE_SETTLEMENT} action="create" />,
    },
    // ── Geographic Zones ──────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Zones,
      route: <PermissionRoute element={<Zones />} resource={RESOURCES.ZONE} action="view" />,
    },
    {
      path: prefix + RoutesLink.AddZone,
      route: <PermissionRoute element={<AddZone />} resource={RESOURCES.ZONE} action="create" />,
    },
    {
      path: prefix + RoutesLink.EditZone,
      route: <PermissionRoute element={<AddZone />} resource={RESOURCES.ZONE} action="update" />,
    },
    // ── Analytics ─────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.Analytics,
      route: <PermissionRoute element={<AnalyticsPage />} resource={RESOURCES.DASHBOARD} action="view" />,
    },
    // ── Payouts ─────────────────────────────────────────────────────────
    {
      path: prefix + RoutesLink.payouts,
      route: <PermissionRoute element={<Payouts />} resource={RESOURCES.PAYOUTS} action="view" />,
    },
  ];


  return (
    <>
      <Routes>
        {ProtectedLoginRoutes.map((route, index) => (
          <Route
            key={`login-${index}`}
            path={route.path}
            element={<ProtectedLoginRoute element={route.route} />}
          />
        ))}

        {/* All Private Routes — generated per role */}
        {privateRoutes.map((route, index) => (
          <Route
            key={`private-${index}`}
            path={route.path}
            element={<PrivateRoute element={route.route} />}
          />
        ))}
        {/* Public Legal & Info Pages (Public access without auth) */}
        <Route path={RoutesLink.faq} element={<FAQPage />} />
        <Route path={RoutesLink.disclaimer} element={<DisclaimerPage />} />
        <Route path={RoutesLink.privacyPolicy} element={<PrivacyPolicyPage />} />
        <Route path={RoutesLink.returnPolicy} element={<ReturnPolicyPage />} />
        <Route path={RoutesLink.returnPolicyAlias} element={<ReturnPolicyPage />} />
        <Route path={RoutesLink.termsAndConditions} element={<TermsConditionsPage />} />

        {/* Global Error/Fallback Routes */}
        <Route path={RoutesLink.forbidden_403} element={<Forbidden403 />} />
        {/* <Route path="*" element={<NotFound />} />  */}
        <Route path="*" element={<Navigate to={prefix ? prefix + RoutesLink.dashboard : RoutesLink.login} replace />} />
      </Routes>
    </>
  );
}

